import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const IntegrationsSection = ({ onShowNotification }) => {
  const [integrations, setIntegrations] = useState([
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Connect ChatGPT and DALL-E for seamless prompt deployment',
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1c6868590-1767071461488.png",
    iconAlt: 'OpenAI logo featuring a stylized circular design with intersecting lines in black and white on a clean white background',
    connected: true,
    apiKey: 'sk-...abc123',
    lastUsed: '2026-01-06'
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Integrate Claude AI for advanced prompt generation',
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_156c9b6a2-1767071461157.png",
    iconAlt: 'Anthropic company logo with modern geometric design featuring orange and black colors on a minimalist white background',
    connected: false,
    apiKey: '',
    lastUsed: null
  },
  {
    id: 'google',
    name: 'Google AI',
    description: 'Connect Gemini for multimodal AI capabilities',
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc57bf86-1767072446961.png",
    iconAlt: 'Google AI logo with colorful G letter in signature Google colors - blue, red, yellow, and green on white background',
    connected: true,
    apiKey: 'AIza...xyz789',
    lastUsed: '2026-01-05'
  },
  {
    id: 'midjourney',
    name: 'Midjourney',
    description: 'Link your Midjourney account for image generation',
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_15d8650ac-1765071829033.png",
    iconAlt: 'Midjourney logo featuring a stylized sailing ship icon in white against a dark purple gradient background',
    connected: false,
    apiKey: '',
    lastUsed: null
  }]
  );

  const [showApiKeyForm, setShowApiKeyForm] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');

  const handleConnect = (integrationId) => {
    setShowApiKeyForm(integrationId);
    setApiKeyInput('');
  };

  const handleDisconnect = (integrationId) => {
    setIntegrations((prev) =>
    prev?.map((int) =>
    int?.id === integrationId ?
    { ...int, connected: false, apiKey: '', lastUsed: null } :
    int
    )
    );
    onShowNotification('success', 'Integration Disconnected', 'The integration has been removed successfully.');
  };

  const handleSaveApiKey = (integrationId) => {
    if (!apiKeyInput?.trim()) {
      onShowNotification('error', 'Invalid API Key', 'Please enter a valid API key.');
      return;
    }

    setIntegrations((prev) =>
    prev?.map((int) =>
    int?.id === integrationId ?
    { ...int, connected: true, apiKey: apiKeyInput, lastUsed: new Date()?.toISOString()?.split('T')?.[0] } :
    int
    )
    );
    setShowApiKeyForm(null);
    setApiKeyInput('');
    onShowNotification('success', 'Integration Connected', 'Your API key has been saved securely.');
  };

  const handleTestConnection = (integrationId) => {
    onShowNotification('success', 'Connection Successful', 'Your integration is working correctly.');
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">API Integrations</h2>
        <p className="text-sm md:text-base text-muted-foreground mt-1">Connect your AI tool accounts for seamless prompt deployment</p>
      </div>
      <div className="bg-muted/50 rounded-lg p-4 md:p-6 flex items-start gap-3">
        <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">Secure API Storage</p>
          <p>Your API keys are encrypted and stored securely. We never share your credentials with third parties.</p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        {integrations?.map((integration) =>
        <div key={integration?.id} className="bg-card rounded-lg border border-border p-6 md:p-8">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-border flex-shrink-0">
                  <Image
                  src={integration?.icon}
                  alt={integration?.iconAlt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{integration?.name}</h3>
                    {integration?.connected &&
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                        <Icon name="CheckCircle" size={14} />
                        Connected
                      </span>
                  }
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{integration?.description}</p>
                  {integration?.connected && integration?.lastUsed &&
                <p className="text-xs text-muted-foreground">Last used: {integration?.lastUsed}</p>
                }
                </div>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:w-48">
                {integration?.connected ?
              <>
                    <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => handleTestConnection(integration?.id)}
                  fullWidth>

                      Test
                    </Button>
                    <Button
                  variant="destructive"
                  size="sm"
                  iconName="Unlink"
                  iconPosition="left"
                  onClick={() => handleDisconnect(integration?.id)}
                  fullWidth>

                      Disconnect
                    </Button>
                  </> :

              <Button
                variant="default"
                size="sm"
                iconName="Link"
                iconPosition="left"
                onClick={() => handleConnect(integration?.id)}
                fullWidth>

                    Connect
                  </Button>
              }
              </div>
            </div>

            {showApiKeyForm === integration?.id &&
          <div className="mt-6 pt-6 border-t border-border space-y-4">
                <Input
              label="API Key"
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e?.target?.value)}
              placeholder="Enter your API key"
              description="Your API key will be encrypted and stored securely" />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                variant="default"
                onClick={() => handleSaveApiKey(integration?.id)}
                iconName="Check"
                iconPosition="left"
                fullWidth>

                    Save API Key
                  </Button>
                  <Button
                variant="outline"
                onClick={() => {
                  setShowApiKeyForm(null);
                  setApiKeyInput('');
                }}
                fullWidth>

                    Cancel
                  </Button>
                </div>
              </div>
          }
          </div>
        )}
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <h3 className="text-lg font-semibold text-foreground mb-4">Need Help?</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Learn how to obtain API keys from different providers and configure your integrations.
        </p>
        <Button variant="outline" iconName="ExternalLink" iconPosition="right">
          View Integration Guide
        </Button>
      </div>
    </div>);

};

export default IntegrationsSection;